import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Loader, Message, Panel } from 'rsuite';
import authFetch from '../../helpers/authFetch';
import { FINISH_LABELS } from './CardAttributeDefaults';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';

const BADGE_COLORS = {
  DECK: '#3498db',
  TRADE_BINDER: '#2ecc71',
};

const BADGE_LABELS = {
  DECK: 'Deck',
  TRADE_BINDER: 'Trade Binder',
};

const PROXY_COLORS = {
  real: '#2ecc71',
  proxy: '#f39c12',
};

const buildColors = (theme) => {
  const t = getThemeColors(theme);
  return {
    muted: t.textMuted,
    link: t.accentSecondary,
    white: '#fff',
    fallbackBadge: t.textFaint,
  };
};

const SPACING = {
  imageWidth: 120,
  imageRadius: 8,
  badgePaddingH: 8,
  badgePaddingV: 2,
  badgeRadius: 4,
  badgeFontSize: 11,
  summaryFontSize: 14,
  locationGap: 12,
  detailFontSize: 13,
  headerGap: 16,
  headerMarginBottom: 20,
  summaryMarginTop: 8,
  loaderPadding: 40,
  errorMarginBottom: 12,
  panelPadding: 12,
  panelHeaderGap: 8,
  detailMarginTop: 8,
  detailGap: 16,
};

const CardLocationModal = ({ open, onClose, scryfallId, cardName, cardObject }) => {
  const navigate = useNavigate();
  const { preferences } = usePreferences();
  const COLORS = buildColors(preferences.theme);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !scryfallId) return;

    const fetchLocations = async () => {
      setLoading(true);
      setError('');
      setLocations([]);

      try {
        const res = await authFetch(`/collections/card-locations/${scryfallId}`);
        if (!res.ok) {
          throw new Error('Failed to fetch card locations');
        }
        const data = await res.json();
        setLocations(data);
      } catch (err) {
        setError(err.message || 'Failed to load locations');
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, [open, scryfallId]);

  const totalReal = locations
    .filter((loc) => !loc.is_proxy)
    .reduce((sum, loc) => sum + (loc.quantity || 0), 0);
  const totalProxy = locations
    .filter((loc) => loc.is_proxy)
    .reduce((sum, loc) => sum + (loc.quantity || 0), 0);

  const deckCount = locations.filter((loc) => loc.collection_type === 'DECK').length;

  const imageUri = cardObject?.image_uris?.normal
    || cardObject?.card_faces?.[0]?.image_uris?.normal
    || null;

  const handleCollectionClick = (e, collectionId) => {
    e.stopPropagation();
    onClose();
    navigate(`/library/${collectionId}`);
  };

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>Card Locations</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Card header with image and name */}
        <div style={{ display: 'flex', gap: SPACING.headerGap, marginBottom: SPACING.headerMarginBottom, alignItems: 'flex-start' }}>
          {imageUri && (
            <img
              src={imageUri}
              alt={cardName}
              style={{ width: SPACING.imageWidth, borderRadius: SPACING.imageRadius }}
            />
          )}
          <div>
            <h4 style={{ margin: 0 }}>{cardName || 'Unknown Card'}</h4>
            <div style={{ marginTop: SPACING.summaryMarginTop, fontSize: SPACING.summaryFontSize, color: COLORS.muted }}>
              <span style={{ color: PROXY_COLORS.real, fontWeight: 'bold' }}>{totalReal}</span>
              {' '}real {totalReal === 1 ? 'copy' : 'copies'},{' '}
              <span style={{ color: PROXY_COLORS.proxy, fontWeight: 'bold' }}>{totalProxy}</span>
              {' '}proxy {totalProxy === 1 ? 'copy' : 'copies'}
            </div>
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: SPACING.loaderPadding }}>
            <Loader size="md" />
          </div>
        )}

        {error && (
          <Message type="error" style={{ marginBottom: SPACING.errorMarginBottom }}>
            {error}
          </Message>
        )}

        {!loading && !error && locations.length === 0 && (
          <Message type="info">This card is not in any of your collections.</Message>
        )}

        {!loading && !error && locations.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.locationGap }}>
            {deckCount > 0 ? (
              <div style={{ fontSize: SPACING.summaryFontSize, color: COLORS.muted, marginBottom: SPACING.summaryMarginTop }}>
                In <span style={{ fontWeight: 'bold', color: COLORS.link }}>{deckCount}</span> {deckCount === 1 ? 'deck' : 'decks'}
              </div>
            ) : (
              <div style={{ fontSize: SPACING.summaryFontSize, color: COLORS.muted, marginBottom: SPACING.summaryMarginTop }}>
                Not in any decks
              </div>
            )}
            {locations.map((loc) => (
              <Panel key={loc.entry_id} shaded style={{ padding: SPACING.panelPadding }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.panelHeaderGap, flexWrap: 'wrap' }}>
                  {/* Collection name link */}
                  <a
                    href={`/collections/${loc.collection_id}`}
                    style={{ color: COLORS.link, fontWeight: 'bold', textDecoration: 'none', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleCollectionClick(e, loc.collection_id);
                    }}
                  >
                    {loc.collection_name}
                  </a>

                  {/* Collection type badge */}
                  <span
                    style={{
                      backgroundColor: BADGE_COLORS[loc.collection_type] || COLORS.fallbackBadge,
                      color: COLORS.white,
                      padding: `${SPACING.badgePaddingV}px ${SPACING.badgePaddingH}px`,
                      borderRadius: SPACING.badgeRadius,
                      fontSize: SPACING.badgeFontSize,
                      fontWeight: 'bold',
                    }}
                  >
                    {BADGE_LABELS[loc.collection_type] || loc.collection_type}
                  </span>

                  {/* Deck type if applicable */}
                  {loc.deck_type && (
                    <span style={{ fontSize: SPACING.badgeFontSize, color: COLORS.muted }}>
                      ({loc.deck_type})
                    </span>
                  )}

                  {/* Real/Proxy badge */}
                  <span
                    style={{
                      backgroundColor: loc.is_proxy ? PROXY_COLORS.proxy : PROXY_COLORS.real,
                      color: COLORS.white,
                      padding: `${SPACING.badgePaddingV}px ${SPACING.badgePaddingH}px`,
                      borderRadius: SPACING.badgeRadius,
                      fontSize: SPACING.badgeFontSize,
                      fontWeight: 'bold',
                    }}
                  >
                    {loc.is_proxy ? 'Proxy' : 'Real'}
                  </span>
                </div>

                {/* Entry details */}
                <div style={{ marginTop: SPACING.detailMarginTop, fontSize: SPACING.detailFontSize, color: COLORS.muted, display: 'flex', gap: SPACING.detailGap, flexWrap: 'wrap' }}>
                  <span>Qty: {loc.quantity}</span>
                  <span>Condition: {loc.condition || 'N/A'}</span>
                  <span>Finish: {FINISH_LABELS[loc.finish] || 'Non-Foil'}</span>
                </div>
              </Panel>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CardLocationModal;
